import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { gymModules } from '../services/api';
import { useAuth } from './AuthContext';

/*
 * Os módulos que o ginásio tem ligados (Definições → Módulos). É a ÚNICA fonte para saber "esta entrada de menu / esta página existe neste ginásio?":
 * o menu lateral, o menu do cabeçalho, as rotas e as páginas perguntam aqui, em vez de cada um ter a sua cópia.
 *
 * - Enquanto o servidor não responde, usa-se a cópia guardada na última visita; sem cópia, o estado é "desconhecido" (null) e quem pergunta recebe FALSO:
 *   é preferível não mostrar um módulo por um instante a mostrar (nem por um fotograma) o de um módulo desligado.
 * - Uma entrada sem módulo (feature vazio) está sempre ligada.
 */

const STORAGE_KEY = 'gym.capabilities';
const CapabilitiesContext = createContext(null);

const readCache = () => {
  try { const raw = localStorage.getItem(STORAGE_KEY); return raw ? JSON.parse(raw) : null; } catch { return null; }
};

export function CapabilitiesProvider({ children }) {
  const { user } = useAuth();
  const [map, setMap] = useState(readCache);
  const [ready, setReady] = useState(false);

  const refresh = useCallback(() => {
    return gymModules.capabilities()
      .then((res) => {
        const next = res.data || {};
        setMap(next);
        try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch { /* sem armazenamento: continua em memória */ }
      })
      .catch(() => { /* mantém a cópia/estado atual */ })
      .finally(() => setReady(true));
  }, []);

  useEffect(() => {
    if (!user) { setReady(false); return; }
    refresh();
  }, [user, refresh]);

  const value = useMemo(() => ({
    capabilities: map,
    // pronto = há resposta do servidor OU uma cópia guardada em que confiar
    ready: ready || map !== null,
    isEnabled: (feature) => {
      if (!feature) return true;
      if (map === null) return false;
      return map[feature] !== false;
    },
    refresh,
  }), [map, ready, refresh]);

  return <CapabilitiesContext.Provider value={value}>{children}</CapabilitiesContext.Provider>;
}

const FALLBACK = { capabilities: null, ready: false, isEnabled: (feature) => !feature, refresh: () => Promise.resolve() };

/** Fora do provider (testes de páginas soltas) nada é gated por módulo: só as entradas sem módulo existem. */
export const useCapabilities = () => useContext(CapabilitiesContext) || FALLBACK;

export default CapabilitiesContext;
